define([
    'santa-components',
    'lodash',
    'componentsCore',
    'tpaComponents',
    'color',
    'coreUtils'
], function (santaComponents, _, componentsCore, tpaComponents, color, coreUtils) {
    'use strict';

    const linkRenderer = coreUtils.linkRenderer,
        tpaMixins = tpaComponents.tpaMixins,
        TPA_LINK_TYPES = {
            ExternalLink: 'WEBSITE',
            EmailLink: 'EMAIL',
            PageLink: 'PAGE',
            DocumentLink: 'DOCUMENT',
            AnchorLink: 'ANCHOR',
            DynamicPageLink: 'DYNAMIC_PAGE_LINK'
        };

    function rgbToHex(rgbAsString) {
        const rgbAsArray = rgbAsString.split(',');

        return color({
            r: rgbAsArray[0],
            g: rgbAsArray[1],
            b: rgbAsArray[2]
        }).hexString();
    }

    function getStyles(generalTheme, styleProps, styleData) {
        let colorValue;
        let alphaValue;
        const map = {};

        _.forEach(styleProps, function (value, key) {
            colorValue = coreUtils.colorParser.getColorValue(generalTheme, styleData[styleProps[key]]);
            map[key] = _.includes(colorValue, ',') ? rgbToHex(colorValue) : colorValue;
            alphaValue = styleData[`alpha-${styleProps[key]}`];
            if (!_.isUndefined(alphaValue)) {
                map[`alpha${coreUtils.stringUtils.capitalize(key)}`] = alphaValue;
            }
        });

        return map;
    }

    function getImageData(imageData, linkRenderInfo, rootNavigationInfo) {
        imageData = imageData.link ? _.merge({}, imageData, linkRenderer.renderLink(imageData.link, linkRenderInfo, rootNavigationInfo), {linkType: TPA_LINK_TYPES[imageData.link.type]}) : imageData;
        return _.assign(imageData, {
            description: coreUtils.filterHtmlString(imageData.description),
            title: coreUtils.filterHtmlString(imageData.title)
        });
    }

    function getImagesData(imagesData, linkRenderInfo, rootNavigationInfo) {
        return _.map(imagesData, image => getImageData(image, linkRenderInfo, rootNavigationInfo));
    }

    function getGalleryIframeUrl(santaBase, galleryType, buildUrl, isDebug) {
        const gallerySource = coreUtils.urlUtils.joinURL(santaBase, `/node_modules/santa-galleries/${isDebug ? 'src/' : 'target/'}`);
        const baseUrl = `${gallerySource + galleryType}/${galleryType}.html`;

        return buildUrl(baseUrl, ['compId', 'deviceType', 'locale', 'viewMode']);
    }

    function getMessage(compProp, items, props, styleProps, styleData) {
        return {
            params: {
                props: _.merge({}, compProp, getStyles(props.THEME_DATA, styleProps, styleData)),
                quality: props.globalImageQuality,
                marketingLandingPage: props.isExperimentOpen('sv_marketingLandingPage'),
                items: getImagesData(items, props.linkRenderInfo, props.rootNavigationInfo),
                mainPageId: props.mainPageId
            },
            eventType: 'SETTINGS_UPDATED',
            intent: 'addEventListener'
        };
    }

    function createEvent(imageData, imageIndex, eventType) {
        return {
            item: imageData,
            timeStamp: coreUtils.loggingUtils.performance.now(),
            imageIndex,
            name: eventType
        };
    }

    function withoutQuality(items) {
        return _.map(items, item => _.omit(item, 'quality'));
    }

    /**
     * @class core.tpaGallery
     *
     * @extends {tpa.mixins.tpaUrlBuilder}
     * @extends {tpa.mixins.tpaCompApi}
     * @extends {core.skinBasedComp}
     */
    return {
        mixins: [componentsCore.mixins.skinBasedComp, tpaMixins.tpaUrlBuilder, tpaMixins.tpaCompApi, componentsCore.mixins.skinInfo],
        statics: {
            compSpecificIsDomOnlyOverride() {
                return false;
            }
        },
        propTypes: {
            id: santaComponents.santaTypesDefinitions.Component.id.isRequired,
            compProp: santaComponents.santaTypesDefinitions.Component.compProp.isRequired,
            compData: santaComponents.santaTypesDefinitions.Component.compData.isRequired,
            linkRenderInfo: santaComponents.santaTypesDefinitions.Link.renderInfo.isRequired,
            rootNavigationInfo: santaComponents.santaTypesDefinitions.Component.rootNavigationInfo.isRequired,
            navigateToPage: santaComponents.santaTypesDefinitions.navigateToPage.isRequired,
            compTheme: santaComponents.santaTypesDefinitions.Component.theme.isRequired,
            style: santaComponents.santaTypesDefinitions.Component.style.isRequired,
            structure: santaComponents.santaTypesDefinitions.Component.structure.isRequired,
            sendPostMessage: santaComponents.santaTypesDefinitions.TPA.sendPostMessage.isRequired,
            santaBase: santaComponents.santaTypesDefinitions.santaBase.isRequired,
            isDebugMode: santaComponents.santaTypesDefinitions.isDebugMode.isRequired,
            globalImageQuality: santaComponents.santaTypesDefinitions.globalImageQuality.isRequired,
            mainPageId: santaComponents.santaTypesDefinitions.mainPageId.isRequired,
            THEME_DATA: santaComponents.santaTypesDefinitions.Theme.THEME_DATA.isRequired,
            isPlayingAllowed: santaComponents.santaTypesDefinitions.RenderFlags.isPlayingAllowed.isRequired,
            tpaComponentsDomAspect: santaComponents.santaTypesDefinitions.SiteAspects.tpaComponentsDomAspect.isRequired,
            isMobileView: santaComponents.santaTypesDefinitions.isMobileView.isRequired,
            isExperimentOpen: santaComponents.santaTypesDefinitions.isExperimentOpen
        },
        messageSent: false,
        isAlive: false,
        componentInIframeReady: false,
        processImageZoom(data) {
            const itemIndex = data.args[0],
                itemAtIndex = this.props.compData.items[itemIndex],
                galleryId = this.props.compData.id,
                zoomLink = linkRenderer.renderImageZoomLink(this.props.linkRenderInfo, this.props.rootNavigationInfo, itemAtIndex, galleryId, undefined, this.props.id);

            if (this.props.rootNavigationInfo.pageItemId === itemAtIndex.id) {
                return;
            }

            const linkUrl = linkRenderer.getLinkUrlFromLinkProps(zoomLink);
            const pageInfo = _.defaults({pageItemAdditionalData: zoomLink['data-page-item-context']},
                coreUtils.wixUrlParser.parseUrl(this.props.linkRenderInfo, linkUrl));
            this.props.navigateToPage(pageInfo);
        },
        processItemClicked(data) {
            const itemIndex = data.args[0],
                itemAtIndex = this.props.compData.items[itemIndex],
                galleryId = this.props.id;

            this.props.handleAction({
                'type': 'comp',
                'name': coreUtils.siteConstants.ACTION_TYPES.ITEM_CLICKED,
                'sourceId': galleryId,
                'pageId': this.props.mainPageId
            }, createEvent(itemAtIndex, itemIndex, coreUtils.siteConstants.ACTION_TYPES.ITEM_CLICKED));
        },
        getInitialState() {
            this.currStyle = this.props.compTheme;
            this.lastRenderedStyleData = {};
            return {
                height: this.props.style.height
            };
        },

        setAppIsAlive() {
            this.isAlive = true;

            this.askToSendIframeMessage(this.props);

            this.props.tpaComponentsDomAspect.markComponentIsAlive(this.props.id);
        },
        setComponentInIframeReady() {
            this.messageSent = false;
            this.componentInIframeReady = true;

            this.askToSendIframeMessage(this.props);
        },
        askToSendIframeMessage(props) {
            const compProp = props.compProp;
            const compData = props.compData;

            if (this.debounceIframe && this.shouldDebounceIframe && this.shouldDebounceIframe(compProp)) {
                this.debounceIframe(compProp, compData, props);
            } else {
                this.sendIframeMessage(compProp, compData, props);
            }
        },
        sendIframeMessage(compProp, compData, props) {
            let message;

            if (this.isAlive && this.componentInIframeReady && !this.messageSent) {
                const compPropsForMessage = this.getOverrideParams ? this.getOverrideParams(compProp) : compProp;
                const styleData = this.getStyleData(props);
                message = getMessage(compPropsForMessage, compData.items, props, this.getStyleProps(), styleData);
                if (this.patchMessageProps) {
                    this.patchMessageProps(message.params.props, props);
                }
                this.props.sendPostMessage(this, message);
                this.lastRenderedStyleData = styleData;
                this.messageSent = true;
            }
        },
        shouldRenderIframe(nextProps) {
            const newItems = getImagesData(nextProps.compData.items, nextProps.linkRenderInfo, nextProps.rootNavigationInfo);

            const newItemsWithoutQuality = withoutQuality(newItems);
            const oldItemsWithoutQuality = withoutQuality(this.props.compData.items);

            const globalQuality = this.props.globalImageQuality;
            const globalQualityChanged = !_.isEqual(globalQuality, nextProps.globalImageQuality) || _.every(this.props.compData.items, function (item) {
                return !_.isEqual(item.quality, globalQuality) && !(_.isEmpty(item.quality) && _.isEmpty(globalQuality));
            });

            const shouldRenderIframe =
                !_.isEqual(nextProps.compProp, this.props.compProp) || !_.isEqual(oldItemsWithoutQuality, newItemsWithoutQuality) || globalQualityChanged || !_.isEqual(this.getStyleData(nextProps), this.lastRenderedStyleData);

            return shouldRenderIframe;
        },
        componentWillReceiveProps(nextProps) {
            const nextStyle = nextProps.compTheme;
            if (!_.isEqual(this.currStyle, nextStyle)) {
                this.currStyle = nextStyle;
                this.messageSent = false;
            }
            if (this.shouldRenderIframe(nextProps)) {
                this.messageSent = false;
            }
            if (!_.isEqual(nextProps.style.height, this.props.style.height)) {
                this.setState({height: nextProps.style.height});
            }

            this.askToSendIframeMessage(nextProps);

            if (this.isPlayingAllowed !== nextProps.isPlayingAllowed) {
                this.isPlayingAllowed = nextProps.isPlayingAllowed;
                this.sendEditModeMessage(this.isPlayingAllowed ? 'site' : 'editor');
            }
        },
        sendEditModeMessage(editMode) {
            if (!this.isAlive || !this.componentInIframeReady) {
                return;
            }

            const message = {
                params: {
                    editMode
                },
                eventType: 'EDIT_MODE_CHANGE',
                intent: 'addEventListener'
            };

            this.props.sendPostMessage(this, message);
        },
        getSkinProperties() {
            const height = this.getHeight ? this.getHeight() : this.state.height || 0;
            const width = this.props.isMobileView ? _.get(this.props, 'structure.layout.width', '100%') : '100%';
            return {
                '': {
                    style: {
                        height,
                        display: 'table',
                        minWidth: 10,
                        minHeight: 10
                    }
                },
                iframe: {
                    style: {
                        height,
                        width,
                        position: 'relative',
                        minHeight: 10,
                        minWidth: 10
                    },
                    className: `tpa-gallery-${this.getGalleryType()}`,
                    'data-src': getGalleryIframeUrl(this.props.santaBase, this.getGalleryType(), this.buildUrl, this.props.isDebugMode)
                }
            };
        }
    };
});
